import { useServices } from '@/app/providers/ServiceContext';
import { Button } from '@/core/ui/components/Button';
import type { TenantBillingInvoice } from '@yawi/types';
import { Download, Loader2, Receipt, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { formatCurrency, formatDate } from '@yawi/infrastructure';

interface InvoiceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  onPay?: () => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'PAID':
      return 'bg-emerald-100 text-emerald-700';
    case 'PENDING':
      return 'bg-amber-100 text-amber-700';
    case 'OVERDUE':
      return 'bg-red-100 text-red-700';
    case 'CANCELLED':
      return 'bg-slate-100 text-slate-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

export const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
  onPay,
}) => {
  const { billingRepo } = useServices();
  const [invoice, setInvoice] = useState<TenantBillingInvoice | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && invoiceId) {
      loadInvoice();
    }
  }, [isOpen, invoiceId]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await billingRepo.getInvoiceById(invoiceId);
      setInvoice(data);
    } catch {
      console.error('Error loading invoice');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-bold text-slate-800">Detalle de factura</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : invoice ? (
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-slate-800">{invoice.invoiceNumber}</h3>
                  <p className="text-slate-500">Periodo: {invoice.period}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice.status)}`}>
                  {invoice.status === 'PAID' ? 'Pagada' :
                   invoice.status === 'PENDING' ? 'Pendiente' :
                   invoice.status === 'OVERDUE' ? 'Vencida' : invoice.status}
                </span>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
                <div>
                  <p className="text-sm text-slate-500">Fecha de emision</p>
                  <p className="font-medium text-slate-800">{formatDate(invoice.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Fecha de vencimiento</p>
                  <p className="font-medium text-slate-800">{formatDate(invoice.dueDate)}</p>
                </div>
                {invoice.paidAt && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Fecha de pago</p>
                    <p className="font-medium text-emerald-600">{formatDate(invoice.paidAt)}</p>
                  </div>
                )}
              </div>

              {/* Breakdown */}
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-4 py-3 font-medium text-slate-700">
                  Desglose
                </div>
                <div className="divide-y">
                  <div className="px-4 py-3 flex justify-between">
                    <span className="text-slate-600">Plan base</span>
                    <span className="font-medium">{formatCurrency(invoice.baseAmount, invoice.currency)}</span>
                  </div>
                  {invoice.extraUnitsAmount > 0 && (
                    <div className="px-4 py-3 flex justify-between">
                      <span className="text-slate-600">Unidades adicionales</span>
                      <span className="font-medium">{formatCurrency(invoice.extraUnitsAmount, invoice.currency)}</span>
                    </div>
                  )}
                  {invoice.extraUsersAmount > 0 && (
                    <div className="px-4 py-3 flex justify-between">
                      <span className="text-slate-600">Usuarios adicionales</span>
                      <span className="font-medium">{formatCurrency(invoice.extraUsersAmount, invoice.currency)}</span>
                    </div>
                  )}
                  {invoice.extraStorageAmount > 0 && (
                    <div className="px-4 py-3 flex justify-between">
                      <span className="text-slate-600">Almacenamiento adicional</span>
                      <span className="font-medium">{formatCurrency(invoice.extraStorageAmount, invoice.currency)}</span>
                    </div>
                  )}
                  {invoice.modulesAmount > 0 && (
                    <div className="px-4 py-3 flex justify-between">
                      <span className="text-slate-600">Modulos premium</span>
                      <span className="font-medium">{formatCurrency(invoice.modulesAmount, invoice.currency)}</span>
                    </div>
                  )}
                  {invoice.discountAmount > 0 && (
                    <div className="px-4 py-3 flex justify-between text-emerald-600">
                      <span>Descuento</span>
                      <span className="font-medium">-{formatCurrency(invoice.discountAmount, invoice.currency)}</span>
                    </div>
                  )}
                  <div className="px-4 py-3 flex justify-between">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-medium">{formatCurrency(invoice.subtotal ?? (invoice.totalAmount - invoice.taxAmount), invoice.currency)}</span>
                  </div>
                  <div className="px-4 py-3 flex justify-between">
                    <span className="text-slate-600">IVA (16%)</span>
                    <span className="font-medium">{formatCurrency(invoice.taxAmount, invoice.currency)}</span>
                  </div>
                  <div className="px-4 py-3 flex justify-between bg-slate-50">
                    <span className="font-bold text-slate-800">Total</span>
                    <span className="font-bold text-slate-800 text-lg">{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
                  </div>
                </div>
              </div>

              {/* Usage Breakdown */}
              {invoice.usageBreakdown && (
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 font-medium text-slate-700">
                    Uso del periodo
                  </div>
                  <div className="p-4 space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Usuarios</span>
                      <span>{invoice.usageBreakdown.users.used} / {invoice.usageBreakdown.users.included}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Unidades</span>
                      <span>{invoice.usageBreakdown.units.used} / {invoice.usageBreakdown.units.included}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Almacenamiento</span>
                      <span>{invoice.usageBreakdown.storage.usedGb} GB / {invoice.usageBreakdown.storage.includedGb} GB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Notificaciones</span>
                      <span>{invoice.usageBreakdown.notifications.used} / {invoice.usageBreakdown.notifications.included}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-500">No se pudo cargar la factura</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {invoice && (
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-between rounded-b-2xl">
            <div>
              {invoice.pdfUrl && (
                <Button
                  variant="secondary"
                  onClick={() => window.open(invoice.pdfUrl!, '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="secondary" onClick={onClose}>
                Cerrar
              </Button>
              {invoice.status === 'PENDING' && onPay && (
                <Button onClick={onPay}>
                  Pagar factura
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetailModal;
